/**
 * AI-Powered Image Processing Service for Eyewear
 *
 * Uses:
 * - Cloudflare Workers AI for background removal (BiRefNet)
 * - Gemini Vision API for intelligent analysis (hinge detection, structure analysis)
 * - Canvas API for image manipulation (cropping, glass effects)
 */

import { GoogleGenAI } from "@google/genai";
import { computeAlphaBoundsFromImage } from "../utils/alphaBounds";

// API endpoints
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.vltryon.com';

// Get Gemini API key from environment
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || '';

// Valid Gemini model for vision/analysis tasks
const GEMINI_MODEL = 'gemini-3-pro-image-preview';
// Gemini 3 Pro Image Preview (Nano Banana Pro) - excellent for image regeneration
// Docs: https://cloud.google.com/vertex-ai/generative-ai/docs/models/gemini/3-pro-image
const GEMINI_IMAGE_MODEL = 'gemini-3-pro-image-preview';

const GEMINI_PRICING = {
  'gemini-2.0-flash-001': {
    inputPerMillion: 0.10,
    outputPerMillion: 0.40,
  },
  'gemini-2.0-flash': {
    inputPerMillion: 0.10,
    outputPerMillion: 0.40,
  },
  'gemini-2.5-flash-image': {
    inputPerMillion: 0.10,
    outputPerMillion: 0.40,
  },
  'gemini-3-pro-image-preview': {
    inputPerMillion: 2.00,
    outputPerMillion: 0, // Output is charged per image, not per token
    perImageCost: 0.134,
  },
};

/**
 * Retry wrapper for async functions
 * Retries once on failure (AI advancements - only once)
 */
const withRetry = async (fn, maxRetries = 1, delay = 1000) => {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      console.warn(`Attempt ${attempt + 1} failed:`, error.message);
      if (attempt < maxRetries) {
        console.log(`Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  throw lastError;
};

/**
 * Calculate cost from token usage
 */
const calculateCost = (usage, model = GEMINI_MODEL, imageCount = 0) => {
  if (!usage) return 0;
  
  // Normalize model string to find matching pricing
  const pricingKey = Object.keys(GEMINI_PRICING).find(k => model.includes(k)) || 'gemini-2.0-flash';
  const pricing = GEMINI_PRICING[pricingKey];

  const inputCost = (usage.promptTokenCount || 0) * pricing.inputPerMillion / 1_000_000;
  const outputCost = (usage.candidatesTokenCount || 0) * pricing.outputPerMillion / 1_000_000;
  
  // Add flat fee for generated images (for Gemini 3 / Imagen models)
  const imageCost = imageCount * (pricing.perImageCost || 0);

  return inputCost + outputCost + imageCost;
};

/**
 * Initialize the Gemini AI client
 */
const getAIClient = () => {
  if (!GEMINI_API_KEY) {
    throw new Error('Gemini API key not configured. Set REACT_APP_GEMINI_API_KEY in your environment.');
  }
  return new GoogleGenAI({ apiKey: GEMINI_API_KEY });
};

/**
 * Convert a data URL to base64 string (without the prefix)
 */
const dataUrlToBase64 = (dataUrl) => {
  if (!dataUrl) return null;
  const parts = dataUrl.split(',');
  return parts.length > 1 ? parts[1] : dataUrl;
};

/**
 * Get MIME type from data URL
 */
const getMimeType = (dataUrl) => {
  if (!dataUrl) return 'image/png';
  const match = dataUrl.match(/^data:([^;]+);/);
  return match ? match[1] : 'image/png';
};

/**
 * Load an image from a URL or data URL
 */
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

// ============================================
// IMAGE VALIDATION & QUALITY CHECK FUNCTIONS
// ============================================

/**
 * Analyze image transparency and content
 * Returns detailed metrics about the image
 */
export const analyzeImageContent = async (imageDataUrl) => {
  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const totalPixels = canvas.width * canvas.height;

  let transparentPixels = 0;
  let opaquePixels = 0;
  let semiTransparentPixels = 0;
  let whitePixels = 0;
  let darkPixels = 0;
  let coloredPixels = 0;

  let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
  let totalBrightness = 0;
  let colorSamples = { r: 0, g: 0, b: 0, count: 0 };

  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const idx = (y * canvas.width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];

      if (a === 0) {
        transparentPixels++;
      } else if (a < 250) {
        semiTransparentPixels++;
      } else {
        opaquePixels++;

        // Track content bounds
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);

        const brightness = (r + g + b) / 3;
        totalBrightness += brightness;

        // Classify pixel color
        if (brightness > 230 && Math.abs(r - g) < 20 && Math.abs(g - b) < 20) {
          whitePixels++;
        } else if (brightness < 50) {
          darkPixels++;
        } else {
          coloredPixels++;
        }

        // Sample colors for dominant color detection
        colorSamples.r += r;
        colorSamples.g += g;
        colorSamples.b += b;
        colorSamples.count++;
      }
    }
  }

  const contentWidth = maxX > minX ? maxX - minX + 1 : 0;
  const contentHeight = maxY > minY ? maxY - minY + 1 : 0;
  const contentArea = contentWidth * contentHeight;
  const avgBrightness = colorSamples.count > 0 ? totalBrightness / colorSamples.count : 0;
  const avgColor = colorSamples.count > 0 ? {
    r: Math.round(colorSamples.r / colorSamples.count),
    g: Math.round(colorSamples.g / colorSamples.count),
    b: Math.round(colorSamples.b / colorSamples.count),
  } : null;

  return {
    dimensions: { width: canvas.width, height: canvas.height },
    contentBounds: contentWidth > 0 ? { minX, minY, maxX, maxY, width: contentWidth, height: contentHeight } : null,
    pixelCounts: {
      total: totalPixels,
      transparent: transparentPixels,
      semiTransparent: semiTransparentPixels,
      opaque: opaquePixels,
      white: whitePixels,
      dark: darkPixels,
      colored: coloredPixels,
    },
    percentages: {
      transparent: (transparentPixels / totalPixels) * 100,
      semiTransparent: (semiTransparentPixels / totalPixels) * 100,
      opaque: (opaquePixels / totalPixels) * 100,
      white: opaquePixels > 0 ? (whitePixels / opaquePixels) * 100 : 0,
      dark: opaquePixels > 0 ? (darkPixels / opaquePixels) * 100 : 0,
    },
    contentFillRatio: contentArea > 0 ? opaquePixels / contentArea : 0,
    avgBrightness,
    avgColor,
    aspectRatio: canvas.width / canvas.height,
    contentAspectRatio: contentWidth > 0 ? contentWidth / contentHeight : 0,
    isDarkFrame: avgBrightness < 80,
    isLightFrame: avgBrightness > 180,
    hasTransparentBackground: (transparentPixels / totalPixels) > 0.15, // Lowered from 0.3 - frames can fill most of image
  };
};

/**
 * Validate that an image meets quality requirements
 */
export const validateImageQuality = async (imageDataUrl, requirements = {}) => {
  const {
    minWidth = 50,
    minHeight = 20,
    minOpaquePixels = 100,
    maxTransparencyPercent = 99,
    minTransparencyPercent = 0,
    requireTransparentBackground = false,
  } = requirements;

  const analysis = await analyzeImageContent(imageDataUrl);
  const issues = [];

  if (analysis.dimensions.width < minWidth) {
    issues.push(`Width too small: ${analysis.dimensions.width}px (min: ${minWidth}px)`);
  }
  if (analysis.dimensions.height < minHeight) {
    issues.push(`Height too small: ${analysis.dimensions.height}px (min: ${minHeight}px)`);
  }
  if (analysis.pixelCounts.opaque < minOpaquePixels) {
    issues.push(`Not enough content: ${analysis.pixelCounts.opaque} opaque pixels (min: ${minOpaquePixels})`);
  }
  if (analysis.percentages.transparent > maxTransparencyPercent) {
    issues.push(`Too transparent: ${analysis.percentages.transparent.toFixed(1)}% (max: ${maxTransparencyPercent}%)`);
  }
  if (analysis.percentages.transparent < minTransparencyPercent) {
    issues.push(`Not transparent enough: ${analysis.percentages.transparent.toFixed(1)}% (min: ${minTransparencyPercent}%)`);
  }
  if (requireTransparentBackground && !analysis.hasTransparentBackground) {
    issues.push('Background not transparent');
  }

  return {
    valid: issues.length === 0,
    issues,
    analysis,
  };
};

/**
 * Determine optimal processing strategy based on image characteristics
 */
export const determineProcessingStrategy = async (imageDataUrl, imageType = 'front') => {
  const analysis = await analyzeImageContent(imageDataUrl);

  const strategy = {
    imageType,
    analysis,
    recommendations: [],
    processingOrder: 'standard', // 'standard', 'crop-first', 'ai-first'
    useAggressiveBgRemoval: false,
    useMultiPassCleanup: false,
    colorAdjustments: null,
  };

  // Dark frames need more careful processing
  if (analysis.isDarkFrame) {
    strategy.recommendations.push('Dark frame detected - using shape-based edge detection');
    strategy.useMultiPassCleanup = true;

    // For dark frames, AI cleanup first might help distinguish frame from background
    if (imageType === 'side') {
      strategy.processingOrder = 'ai-first';
      strategy.recommendations.push('AI cleanup before background removal for better dark frame handling');
    }
  }

  // High white pixel percentage suggests background issues
  if (analysis.percentages.white > 30 && !analysis.hasTransparentBackground) {
    strategy.recommendations.push('High white content - may need aggressive background removal');
    strategy.useAggressiveBgRemoval = true;
  }

  // Very small content area relative to image - likely needs cropping first
  if (analysis.contentFillRatio < 0.3 && analysis.contentBounds) {
    strategy.recommendations.push('Low content density - cropping first recommended');
    if (imageType === 'side') {
      strategy.processingOrder = 'crop-first';
    }
  }

  // Side images with very wide aspect ratio - temple already isolated
  if (imageType === 'side' && analysis.contentAspectRatio > 4) {
    strategy.recommendations.push('Temple appears already isolated (high aspect ratio)');
    strategy.processingOrder = 'standard';
  }

  // Side images with low aspect ratio - frame likely visible
  if (imageType === 'side' && analysis.contentAspectRatio < 2 && analysis.contentAspectRatio > 0) {
    strategy.recommendations.push('Frame front likely visible - AI cleanup critical');
    strategy.useMultiPassCleanup = true;
  }

  return strategy;
};

/**
 * Compare two images and detect if processing improved quality
 */
/**
 * Enhance image quality: contrast, sharpness, color balance
 * Useful for improving AI cleanup results
 */
export const enhanceImageQuality = async (imageDataUrl, options = {}) => {
  const {
    contrast = 1.0,        // 1.0 = no change, >1 = more contrast
    brightness = 1.0,      // 1.0 = no change
    saturation = 1.0,      // 1.0 = no change, >1 = more vivid
    sharpen = false,       // Apply sharpening
    removeColorCast = true, // Remove color cast from white/gray areas
  } = options;

  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Step 1: Apply contrast and brightness
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 20) continue; // Skip transparent pixels

    // Apply brightness and contrast
    for (let c = 0; c < 3; c++) {
      let value = data[i + c];
      // Contrast: move away from middle gray (128)
      value = ((value / 255 - 0.5) * contrast + 0.5) * 255;
      // Brightness
      value *= brightness;
      data[i + c] = Math.max(0, Math.min(255, Math.round(value)));
    }
  }

  // Step 2: Apply saturation adjustment
  if (saturation !== 1.0) {
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 20) continue;

      const r = data[i], g = data[i + 1], b = data[i + 2];
      const gray = 0.299 * r + 0.587 * g + 0.114 * b;

      data[i] = Math.max(0, Math.min(255, Math.round(gray + (r - gray) * saturation)));
      data[i + 1] = Math.max(0, Math.min(255, Math.round(gray + (g - gray) * saturation)));
      data[i + 2] = Math.max(0, Math.min(255, Math.round(gray + (b - gray) * saturation)));
    }
  }

  // Step 3: Remove color cast from near-white/gray areas
  if (removeColorCast) {
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 20) continue;

      const r = data[i], g = data[i + 1], b = data[i + 2];
      const brightness = (r + g + b) / 3;

      // For bright pixels that should be white/gray, neutralize color cast
      if (brightness > 200) {
        const maxDiff = Math.max(Math.abs(r - g), Math.abs(g - b), Math.abs(r - b));
        if (maxDiff < 40) { // Nearly gray
          const neutral = Math.round(brightness);
          const blend = (brightness - 200) / 55; // Blend more as it gets brighter
          data[i] = Math.round(r + (neutral - r) * blend * 0.5);
          data[i + 1] = Math.round(g + (neutral - g) * blend * 0.5);
          data[i + 2] = Math.round(b + (neutral - b) * blend * 0.5);
        }
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);

  // Step 4: Apply sharpening using convolution
  if (sharpen) {
    const sharpened = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const src = new Uint8ClampedArray(data);
    const width = canvas.width;
    const height = canvas.height;

    // Sharpening kernel (unsharp mask style)
    const kernel = [
      0, -0.5, 0,
      -0.5, 3, -0.5,
      0, -0.5, 0
    ];

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        if (src[idx + 3] < 20) continue; // Skip transparent

        for (let c = 0; c < 3; c++) {
          let sum = 0;
          for (let ky = -1; ky <= 1; ky++) {
            for (let kx = -1; kx <= 1; kx++) {
              const kidx = ((y + ky) * width + (x + kx)) * 4;
              sum += src[kidx + c] * kernel[(ky + 1) * 3 + (kx + 1)];
            }
          }
          sharpened.data[idx + c] = Math.max(0, Math.min(255, Math.round(sum)));
        }
      }
    }

    ctx.putImageData(sharpened, 0, 0);
  }

  return canvas.toDataURL('image/png');
};

/**
 * Clean up edges of transparent image - remove fringing/halos
 */
export const cleanupTransparentEdges = async (imageDataUrl, options = {}) => {
  const {
    edgeWidth = 2,        // Pixels from edge to check
    alphaThreshold = 200, // Consider as edge if alpha below this
  } = options;

  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const width = canvas.width;
  const height = canvas.height;

  // Find edge pixels and their interior neighbors
  const getIdx = (x, y) => (y * width + x) * 4;

  // Mark pixels that are on the transparent edge
  const edgePixels = new Set();

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = getIdx(x, y);
      const alpha = data[idx + 3];

      // Skip fully transparent
      if (alpha === 0) continue;

      // Check if this pixel is near a transparent pixel
      let nearTransparent = false;
      for (let dy = -edgeWidth; dy <= edgeWidth && !nearTransparent; dy++) {
        for (let dx = -edgeWidth; dx <= edgeWidth && !nearTransparent; dx++) {
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || nx >= width || ny < 0 || ny >= height) {
            nearTransparent = true; // Image edge counts as transparent
            continue;
          }
          const nidx = getIdx(nx, ny);
          if (data[nidx + 3] === 0) {
            nearTransparent = true;
          }
        }
      }

      if (nearTransparent && alpha < alphaThreshold) {
        edgePixels.add(`${x},${y}`);
      }
    }
  }

  // For edge pixels with low alpha, blend color toward interior color
  for (const key of edgePixels) {
    const [x, y] = key.split(',').map(Number);
    const idx = getIdx(x, y);

    // Find nearest fully opaque interior pixel
    let bestInterior = null;
    let bestDist = Infinity;

    for (let dy = -3; dy <= 3; dy++) {
      for (let dx = -3; dx <= 3; dx++) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
        const nidx = getIdx(nx, ny);
        if (data[nidx + 3] > 250 && !edgePixels.has(`${nx},${ny}`)) {
          const dist = dx * dx + dy * dy;
          if (dist < bestDist) {
            bestDist = dist;
            bestInterior = nidx;
          }
        }
      }
    }

    // Blend edge pixel toward interior color
    if (bestInterior !== null) {
      const alpha = data[idx + 3] / 255;
      const blendFactor = 0.7; // How much to blend toward interior

      data[idx] = Math.round(data[idx] * (1 - blendFactor) + data[bestInterior] * blendFactor);
      data[idx + 1] = Math.round(data[idx + 1] * (1 - blendFactor) + data[bestInterior + 1] * blendFactor);
      data[idx + 2] = Math.round(data[idx + 2] * (1 - blendFactor) + data[bestInterior + 2] * blendFactor);
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
};

export const compareProcessingResult = async (beforeDataUrl, afterDataUrl) => {
  const before = await analyzeImageContent(beforeDataUrl);
  const after = await analyzeImageContent(afterDataUrl);

  const improvements = [];
  const regressions = [];

  // Check transparency improvement
  const transparencyChange = after.percentages.transparent - before.percentages.transparent;
  if (transparencyChange > 10) {
    improvements.push(`Background removal: +${transparencyChange.toFixed(1)}% transparent`);
  } else if (transparencyChange < -10) {
    regressions.push(`Lost content: ${Math.abs(transparencyChange).toFixed(1)}% became transparent`);
  }

  // Check if content bounds changed significantly (cropping)
  if (before.contentBounds && after.contentBounds) {
    const beforeArea = before.contentBounds.width * before.contentBounds.height;
    const afterArea = after.contentBounds.width * after.contentBounds.height;
    const areaChange = ((afterArea - beforeArea) / beforeArea) * 100;

    if (areaChange < -20) {
      // Lost significant content - might be a problem
      if (after.pixelCounts.opaque < before.pixelCounts.opaque * 0.5) {
        regressions.push(`Lost significant content: ${Math.abs(areaChange).toFixed(1)}% smaller`);
      } else {
        improvements.push(`Cropped unnecessary areas: ${Math.abs(areaChange).toFixed(1)}% smaller`);
      }
    }
  }

  // Check aspect ratio change for temple images
  const aspectRatioChange = after.contentAspectRatio - before.contentAspectRatio;
  if (aspectRatioChange > 1) {
    improvements.push('Improved temple isolation (more elongated)');
  }

  return {
    improved: improvements.length > 0 && regressions.length === 0,
    improvements,
    regressions,
    before: {
      dimensions: before.dimensions,
      contentBounds: before.contentBounds,
      transparencyPercent: before.percentages.transparent,
      opaquePixels: before.pixelCounts.opaque,
    },
    after: {
      dimensions: after.dimensions,
      contentBounds: after.contentBounds,
      transparencyPercent: after.percentages.transparent,
      opaquePixels: after.pixelCounts.opaque,
    },
  };
};

/**
 * Remove background using Cloudflare Workers AI (BiRefNet model)
 * This provides much better results than client-side processing
 * Now with retry logic for improved reliability
 */
export const removeBackgroundWorkersAI = async (imageDataUrl, options = {}) => {
  const { enableRetry = true } = options;
  const base64 = dataUrlToBase64(imageDataUrl);
  const mimeType = getMimeType(imageDataUrl);

  const fetchBackground = async () => {
    const response = await fetch(`${API_BASE_URL}/api/image-processing/remove-background`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        imageBase64: base64,
        mimeType,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Background removal failed');
    }

    const result = await response.json();

    if (result.success && result.imageBase64) {
      // Convert back to data URL
      return `data:${result.mimeType || 'image/png'};base64,${result.imageBase64}`;
    }

    throw new Error('No image returned from Workers AI');
  };

  try {
    // Use retry wrapper if enabled (retry once on failure)
    if (enableRetry) {
      return await withRetry(fetchBackground, 1, 1500);
    }
    return await fetchBackground();
  } catch (error) {
    console.error('Workers AI background removal error:', error);
    // Fall back to client-side processing
    console.log('Falling back to client-side background removal...');
    return removeBackgroundStrong(imageDataUrl);
  }
};

/**
 * Analyze eyewear image using Gemini Vision
 */
export const analyzeEyewearImage = async (imageDataUrl, imageType = 'front') => {
  const ai = getAIClient();
  const base64 = dataUrlToBase64(imageDataUrl);
  const mimeType = getMimeType(imageDataUrl);

  const prompt = imageType === 'front'
    ? `You are an expert eyewear analyst. Analyze this front-facing eyewear/glasses image.

Provide a JSON response with the following structure:
{
  "frameAnalysis": {
    "type": "full-rim" | "half-rim" | "rimless",
    "shape": "round" | "square" | "rectangle" | "aviator" | "cat-eye" | "oval" | "other",
    "hasLenses": boolean,
    "hasTintedLenses": boolean,
    "isSunglasses": boolean (true if dark/tinted lenses typical of sunglasses),
    "lensTintColor": [r, g, b] or null (RGB color of lens tint, null if clear),
    "lensTintOpacity": 0-1 (how opaque/dark the lenses are, 0=clear, 1=fully opaque)
  },
  "boundingBox": {
    "left": 0-1,
    "right": 0-1,
    "top": 0-1,
    "bottom": 0-1
  },
  "hingePositions": {
    "left": {
      "x": 0-1 (normalized x position where left temple attaches - should be near left edge),
      "y": 0-1 (normalized y position - typically around 0.3-0.5)
    },
    "right": {
      "x": 0-1 (normalized x position where right temple attaches - should be near right edge),
      "y": 0-1 (normalized y position - typically around 0.3-0.5)
    }
  },
  "lensAreas": [
    {
      "centerX": 0-1,
      "centerY": 0-1,
      "radiusX": 0-1,
      "radiusY": 0-1
    }
  ],
  "confidence": 0-1
}

CRITICAL RULES:
- Coordinates are normalized 0-1 (0,0 = top-left, 1,1 = bottom-right)
- LEFT hinge x should be LOW (0.0-0.15) - it's on the left side of frame
- RIGHT hinge x should be HIGH (0.85-1.0) - it's on the right side of frame
- Hinge y is typically around 0.3-0.5 (upper-middle of frame)
- SUNGLASSES DETECTION: Set isSunglasses=true if lenses are dark/tinted (not clear prescription lenses)
- If lenses have any color tint (gray, brown, blue, green, etc), set hasTintedLenses=true and provide RGB
- lensTintColor should be the actual color of the lens tint as [R,G,B] values 0-255
- lensTintOpacity: estimate how opaque the lenses are (0=completely clear, 0.5=50% tinted, 1=mirror/fully opaque)`
    : `You are an expert eyewear analyst. This image shows a SIDE VIEW of eyeglasses.

TASK: Identify EXACTLY where to crop to get ONLY the temple arm (the thin piece that goes over the ear), EXCLUDING the frame front (which has the lens opening).

VISUAL IDENTIFICATION:
- FRAME FRONT: The part with the LENS - usually has a curved/round opening where glass would be. This is WIDER and may show WHITE or TRANSPARENT area where the lens is.
- TEMPLE ARM: A long THIN piece (like a rod/stick) that extends toward the ear. It starts at the HINGE (small metal joint) and ends at the ear tip. NO lens opening, just solid material.

In this image, look for:
1. Is there a LENS AREA visible? (round/oval opening, possibly white/light colored)
2. Where does the thin temple ARM begin? (usually right after the hinge, where the lens area ends)

Provide a JSON response:
{
  "hasLensAreaVisible": boolean (true if you can see a lens/frame front section),
  "lensAreaLocation": "left" | "right" | "none" (which side of image has the lens/frame),
  "templeArmBounds": {
    "startX": 0-1 (x where temple ARM begins - AFTER the lens/frame area),
    "endX": 0-1 (x where temple arm ends at ear tip),
    "top": 0-1,
    "bottom": 0-1
  },
  "hingePosition": {
    "x": 0-1 (x of the hinge - where temple connects to frame),
    "y": 0-1
  },
  "cropBounds": {
    "left": 0-1 (MUST exclude ANY lens/frame area),
    "right": 0-1,
    "top": 0-1,
    "bottom": 0-1
  },
  "needsHorizontalFlip": boolean,
  "confidence": 0-1
}

CRITICAL CROPPING RULES:
- If lensAreaLocation is "right": set cropBounds.right to EXCLUDE the lens area (cut it off)
- If lensAreaLocation is "left": set cropBounds.left to EXCLUDE the lens area (cut it off)
- The result should contain ONLY the thin temple arm, NO lens/frame visible
- Be AGGRESSIVE - crop closer to the hinge to ensure NO lens area remains
- For VTO, hinge must end up on RIGHT side: set needsHorizontalFlip=true if hinge is currently on left`;

  try {
    const response = await ai.models.generateContent({
      model: GEMINI_MODEL,
      contents: {
        parts: [
          { inlineData: { mimeType, data: base64 } },
          { text: prompt },
        ],
      },
      generationConfig: {
        responseMimeType: 'application/json',
      },
    });

    const text = response.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!text) {
      throw new Error('No response from Gemini');
    }

    // Parse JSON response
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Could not parse JSON from response');
    }

    // Extract usage metadata
    const usage = response.usageMetadata ? {
      promptTokenCount: response.usageMetadata.promptTokenCount || 0,
      candidatesTokenCount: response.usageMetadata.candidatesTokenCount || 0,
      totalTokenCount: response.usageMetadata.totalTokenCount || 0,
    } : null;

    const cost = calculateCost(usage, GEMINI_MODEL);
    const analysisResult = JSON.parse(jsonMatch[0]);

    return {
      ...analysisResult,
      _usage: usage,
      _cost: cost,
      _model: GEMINI_MODEL,
    };
  } catch (error) {
    console.error('Gemini analysis error:', error);
    throw error;
  }
};

/**
 * Removes a specific solid background color (Green Screen effect)
 * Used when AI can't generate true transparency
 */
export const removeGreenScreen = async (imageDataUrl, targetColor = [0, 255, 0], tolerance = 60) => {
  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    // Calculate difference from target green
    // Simple distance formula
    const dist = Math.sqrt(
      Math.pow(r - targetColor[0], 2) +
      Math.pow(g - targetColor[1], 2) +
      Math.pow(b - targetColor[2], 2)
    );

    // If it's close to green, make it transparent
    if (dist < tolerance) {
      data[i + 3] = 0; // Alpha = 0
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
};

/**
 * AI-powered temple cleanup using Gemini image editing
 * Simplified instruction: Generate one identical handle, remove everything else
 */
export const cleanupTempleWithAI = async (imageDataUrl) => {
  const ai = getAIClient();
  const base64 = dataUrlToBase64(imageDataUrl);
  const mimeType = getMimeType(imageDataUrl);

  const prompt = `Edit this image:
1. ISOLATION: Generate just ONE temple arm (handle).
   - Remove the frame front, lenses, and the other temple arm.
   - Keep the hinge mechanism on the right side.
2. APPEARANCE: The handle must be IDENTICAL to the original (same texture, color, pattern).
3. BACKGROUND: Place on a Solid Light Grey background (Hex #F0F0F0).`;

  try {
    const response = await withRetry(async () => {
      return await ai.models.generateContent({
        model: GEMINI_IMAGE_MODEL,
        contents: {
          parts: [
            { inlineData: { mimeType, data: base64 } },
            { text: prompt },
          ],
        },
      });
    }, 1);

    const usage = response.usageMetadata ? {
      promptTokenCount: response.usageMetadata.promptTokenCount || 0,
      candidatesTokenCount: response.usageMetadata.candidatesTokenCount || 0,
      totalTokenCount: response.usageMetadata.totalTokenCount || 0,
    } : null;

    const cost = calculateCost(usage, GEMINI_IMAGE_MODEL, 1);

    const candidates = response.candidates;
    if (candidates && candidates.length > 0 && candidates[0]?.content?.parts) {
      for (const part of candidates[0].content.parts) {
        if (part.inlineData) {
          let aiImage = `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
          
          // Use Neural Network for perfect edges (no green halos)
          try {
             const transparentImage = await removeBackgroundWorkersAI(aiImage, { enableRetry: true });
             return {
                dataUrl: transparentImage,
                _usage: usage,
                _cost: cost,
                _model: GEMINI_IMAGE_MODEL,
             };
          } catch (bgError) {
             return {
                dataUrl: aiImage,
                _usage: usage,
                _cost: cost,
                _model: GEMINI_IMAGE_MODEL,
             };
          }
        }
      }
    }
    throw new Error('No image data returned from Gemini');
  } catch (error) {
    console.error('Temple cleanup AI error:', error);
    throw error;
  }
};

/**
 * AI-powered front frame cleanup using Gemini image editing
 * Simplified instruction: Remove background & handles visible through lenses
 * @param {string} imageDataUrl - The image to process
 * @param {Object} options - Processing options
 * @param {boolean} options.isSunglasses - If true, preserve tinted lens color instead of making transparent
 * @param {number} options.lensOpacity - Opacity for sunglasses lenses (0.3-0.9)
 */
export const cleanupFrontFrameWithAI = async (imageDataUrl, options = {}) => {
  const { isSunglasses = false, lensOpacity = 0.5 } = options;
  const ai = getAIClient();
  const base64 = dataUrlToBase64(imageDataUrl);
  const mimeType = getMimeType(imageDataUrl);

  // Different prompts for sunglasses vs prescription glasses
  const prompt = isSunglasses
    ? `Edit this image of SUNGLASSES:
1. BACKGROUND: Place the object on a Solid Light Grey background (Hex #F0F0F0).
2. TINTED LENSES: These are SUNGLASSES with tinted/dark lenses - PRESERVE THE LENS COLOR.
   - Keep the exact same tint color as the original (gray, brown, blue, green, etc).
   - The lenses should remain semi-opaque/tinted, NOT transparent or white.
   - DO NOT make the lenses clear, white, or fully transparent - keep their original tint.
3. HANDLES: Remove/erase any temple arms (handles) that are visible BEHIND the lenses.
   - The lens tint should hide what's behind it, not show the handles through.
4. FRAME: Keep the rim and bridge exactly identical.
IMPORTANT: This is SUNGLASSES - the dark/colored lenses are intentional, preserve that tint color!`
    : `Edit this image of eyewear:
1. BACKGROUND: Place the object on a Solid Light Grey background (Hex #F0F0F0).
2. LENSES: Remove the lens glass COMPLETELY.
   - I want empty, hollow space where the lenses used to be.
   - We should see the background clearly through the empty frame rims.
3. HANDLES: Because the lenses are removed, the handles (temple arms) behind them must be ERASED.
4. FRAME: Keep the rim and bridge exactly identical.`;

  try {
    const response = await withRetry(async () => {
      return await ai.models.generateContent({
        model: GEMINI_IMAGE_MODEL,
        contents: {
          parts: [
            { inlineData: { mimeType, data: base64 } },
            { text: prompt },
          ],
        },
      });
    }, 1); 

    const usage = response.usageMetadata ? {
      promptTokenCount: response.usageMetadata.promptTokenCount || 0,
      candidatesTokenCount: response.usageMetadata.candidatesTokenCount || 0,
      totalTokenCount: response.usageMetadata.totalTokenCount || 0,
    } : null;
    const cost = calculateCost(usage, GEMINI_IMAGE_MODEL, 1);

    const candidates = response.candidates;
    if (candidates && candidates.length > 0 && candidates[0]?.content?.parts) {
      for (const part of candidates[0].content.parts) {
        if (part.inlineData) {
          let aiImage = `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
          
          try {
             // Removing the Grey Background makes the lens holes transparent too
             const transparentImage = await removeBackgroundWorkersAI(aiImage, { enableRetry: true });
             return {
                dataUrl: transparentImage,
                _usage: usage,
                _cost: cost,
                _model: GEMINI_IMAGE_MODEL,
             };
          } catch (bgError) {
             return { dataUrl: aiImage, _usage: usage, _cost: cost, _model: GEMINI_IMAGE_MODEL };
          }
        }
      }
    }
    throw new Error('No image data returned from Gemini');
  } catch (error) {
    console.error('Front frame cleanup AI error:', error);
    throw error;
  }
};

/**
 * Generate missing side/temple image from front and angle images
 * Uses Gemini to synthesize a side view
 */
export const generateMissingSideImage = async (frontImageDataUrl, angleImageDataUrl) => {
  const ai = getAIClient();
  const frontBase64 = dataUrlToBase64(frontImageDataUrl);
  const angleBase64 = angleImageDataUrl ? dataUrlToBase64(angleImageDataUrl) : null;
  const mimeType = getMimeType(frontImageDataUrl);

  const parts = [
    { inlineData: { mimeType, data: frontBase64 } },
  ];

  if (angleBase64) {
    parts.push({ inlineData: { mimeType, data: angleBase64 } });
  }

  const prompt = angleBase64
    ? `You are an expert eyewear product photographer creating images for virtual try-on apps.

I'm providing two images of the same eyeglasses:
1. FRONT VIEW - showing the frame face-on
2. ANGLE VIEW - showing partial side perspective

TASK: Generate a photorealistic SIDE VIEW of just the TEMPLE ARM (the thin piece that goes over the ear).

ANALYZE THE INPUT IMAGES:
- Look at the temple arms visible in both images
- Note the EXACT color (could be black, tortoise, gold, silver, colored, etc.)
- Note the MATERIAL (plastic, metal, acetate, titanium)
- Note the SHAPE (straight, curved, tapered, decorated)
- Note any PATTERNS or DETAILS (logos, textures, gradients)

GENERATE THE TEMPLE:
- Create a pure SIDE VIEW (90 degrees from front)
- Match EXACTLY the color, material, and style from the input images
- Temple should be realistic and photographic quality
- Typical temple length: 135-145mm proportionally
- Include the small hinge mechanism at the attachment point
- Show natural curves and thickness variations

ORIENTATION:
- Hinge point on the RIGHT side of the image
- Ear tip extends to the LEFT
- Temple appears horizontal

OUTPUT REQUIREMENTS:
- PNG with 100% TRANSPARENT background (alpha = 0)
- NO frame front, NO lens area, NO bridge - ONLY the temple arm
- The temple should appear to "float" on transparency
- High resolution, e-commerce product quality`
    : `You are an expert eyewear product photographer creating images for virtual try-on apps.

I'm providing a FRONT VIEW of eyeglasses showing the frame face-on.

TASK: Generate a photorealistic SIDE VIEW of just the TEMPLE ARM (the thin piece that goes over the ear).

ANALYZE THE FRONT VIEW:
- Look at where the temples attach at the hinges (left and right edges)
- Note the visible portion of temples extending backward
- Identify the EXACT color of the frame/temples
- Identify the MATERIAL (plastic, metal, acetate, tortoise shell, etc.)
- Note any patterns, logos, or decorative elements

GENERATE A MATCHING TEMPLE:
- Create a temple arm that MATCHES the frame style perfectly
- Use the SAME color as seen in the front view
- Use the SAME material appearance (glossy plastic, brushed metal, etc.)
- Typical temple shapes: straight, slightly curved, tapered toward ear
- Include realistic details: hinge mechanism, any brand logos/text if visible
- Typical proportions: thin near hinge, may widen slightly, curves at ear

ORIENTATION:
- Side view (90 degrees from front)
- Hinge attachment point on the RIGHT side of image
- Ear tip extends to the LEFT
- Temple appears roughly horizontal

OUTPUT REQUIREMENTS:
- PNG with 100% TRANSPARENT background (alpha = 0)
- Show ONLY the temple arm - NO frame front, NO lens, NO bridge
- Temple should "float" on transparent background
- Photorealistic, high-resolution, e-commerce quality`;

  parts.push({ text: prompt });

  try {
    const response = await withRetry(async () => {
      return await ai.models.generateContent({
        model: GEMINI_IMAGE_MODEL,
        contents: { parts },
      });
    }, 1); // Retry once on failure

    const usage = response.usageMetadata ? {
      promptTokenCount: response.usageMetadata.promptTokenCount || 0,
      candidatesTokenCount: response.usageMetadata.candidatesTokenCount || 0,
      totalTokenCount: response.usageMetadata.totalTokenCount || 0,
    } : null;

    const cost = calculateCost(usage, GEMINI_IMAGE_MODEL);

    const candidates = response.candidates;
    if (candidates && candidates.length > 0 && candidates[0]?.content?.parts) {
      for (const part of candidates[0].content.parts) {
        if (part.inlineData) {
          return {
            dataUrl: `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`,
            _usage: usage,
            _cost: cost,
            _model: GEMINI_IMAGE_MODEL,
          };
        }
      }
    }

    throw new Error('No image data returned from Gemini');
  } catch (error) {
    console.error('Side image generation error:', error);
    throw error;
  }
};

/**
 * Apply semi-transparent lens effect for sunglasses
 * Instead of erasing lens areas, applies 0.5 opacity
 */
export const applySunglassLensOpacity = async (imageDataUrl, lensAreas, options = {}) => {
  const {
    opacity = 0.6,
    // Default to a dark sunglass grey if no color is provided
    tintColor = [60, 60, 70], 
  } = options;

  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  if (!lensAreas || lensAreas.length === 0) {
    return canvas.toDataURL('image/png');
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  for (const lens of lensAreas) {
    const centerX = lens.centerX * canvas.width;
    const centerY = lens.centerY * canvas.height;
    
    // CRITICAL FIX 1: Shrink the mask by 10% (0.9)
    // This prevents the transparency from hitting the frame rim (the "white edges" issue)
    const radiusX = (lens.radiusX * canvas.width) * 0.90;
    const radiusY = (lens.radiusY * canvas.height) * 0.90;

    // Iterate over the bounding box of the lens to save performance
    const startX = Math.max(0, Math.floor(centerX - radiusX));
    const endX = Math.min(canvas.width, Math.ceil(centerX + radiusX));
    const startY = Math.max(0, Math.floor(centerY - radiusY));
    const endY = Math.min(canvas.height, Math.ceil(centerY + radiusY));

    for (let y = startY; y < endY; y++) {
      for (let x = startX; x < endX; x++) {
        // Check if point is inside the ellipse
        const dx = (x - centerX) / radiusX;
        const dy = (y - centerY) / radiusY;
        
        // Use a slightly softer edge calculation
        if ((dx * dx + dy * dy) <= 1) {
          const idx = (y * canvas.width + x) * 4;
          const currentAlpha = data[idx + 3];

          // CRITICAL FIX 2: Handle both Solid and Empty pixels
          
          if (currentAlpha < 20) {
            // CASE A: The lens is missing/invisible (Alpha ~0)
            // ACTION: Fill it in! (Reconstruct the lens)
            data[idx] = tintColor[0];     // R
            data[idx + 1] = tintColor[1]; // G
            data[idx + 2] = tintColor[2]; // B
            data[idx + 3] = Math.round(255 * opacity); // Set target opacity
          } else {
            // CASE B: The lens exists but might be the wrong color/opacity
            // ACTION: Blend it toward the target tint
            
            // Simple blend: 50% original color + 50% target tint
            data[idx] = Math.round((data[idx] + tintColor[0]) / 2);
            data[idx + 1] = Math.round((data[idx + 1] + tintColor[1]) / 2);
            data[idx + 2] = Math.round((data[idx + 2] + tintColor[2]) / 2);
            
            // Force the opacity to the target level
            data[idx + 3] = Math.round(255 * opacity); 
          }
        }
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
};

/**
 * Remove background using improved flood fill with multiple passes
 * Also removes white/light areas inside the image (like lens areas)
 * Enhanced to handle ANY background color, not just white
 */
export const removeBackgroundStrong = async (imageDataUrl, options = {}) => {
  const {
    tolerance = 60,           // Higher tolerance for better detection
    whiteTolerance = 75,      // Extra tolerance for white/light backgrounds
    passes = 4,               // Multiple passes for better results
    removeInternalWhite = true, // Also remove white areas inside (lenses)
    detectBackgroundColor = true, // Auto-detect background color from corners
  } = options;

  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const width = canvas.width;
  const height = canvas.height;

  const getPixelIndex = (x, y) => (y * width + x) * 4;

  const getPixelColor = (x, y) => {
    const idx = getPixelIndex(x, y);
    return [data[idx], data[idx + 1], data[idx + 2], data[idx + 3]];
  };

  const setPixelTransparent = (x, y) => {
    const idx = getPixelIndex(x, y);
    data[idx + 3] = 0;
  };

  const colorDistance = (c1, c2) => {
    return Math.sqrt(
      Math.pow(c1[0] - c2[0], 2) +
      Math.pow(c1[1] - c2[1], 2) +
      Math.pow(c1[2] - c2[2], 2)
    );
  };

  const isWhitish = (c) => {
    return c[0] > 210 && c[1] > 210 && c[2] > 210;
  };

  const isVeryLight = (c) => {
    return c[0] > 230 && c[1] > 230 && c[2] > 230;
  };

  const isLightBackground = (c) => {
    const brightness = (c[0] + c[1] + c[2]) / 3;
    return brightness > 190;
  };

  const isLightGray = (c) => {
    const brightness = (c[0] + c[1] + c[2]) / 3;
    const maxDiff = Math.max(
      Math.abs(c[0] - brightness),
      Math.abs(c[1] - brightness),
      Math.abs(c[2] - brightness)
    );
    return brightness > 180 && maxDiff < 25; // Light and grayish (not colorful)
  };

  // Auto-detect background color from corners (most likely to be background)
  let detectedBgColor = null;
  if (detectBackgroundColor) {
    const cornerSamples = [
      getPixelColor(0, 0),
      getPixelColor(width - 1, 0),
      getPixelColor(0, height - 1),
      getPixelColor(width - 1, height - 1),
      getPixelColor(Math.floor(width / 2), 0), // top center
      getPixelColor(Math.floor(width / 2), height - 1), // bottom center
    ];

    // Find the most common corner color (likely background)
    const colorCounts = new Map();
    for (const c of cornerSamples) {
      if (c[3] < 200) continue; // Skip if already semi-transparent
      // Quantize to reduce variations
      const key = `${Math.floor(c[0]/20)*20},${Math.floor(c[1]/20)*20},${Math.floor(c[2]/20)*20}`;
      colorCounts.set(key, (colorCounts.get(key) || 0) + 1);
    }

    let maxCount = 0;
    for (const [key, count] of colorCounts) {
      if (count > maxCount) {
        maxCount = count;
        const [r, g, b] = key.split(',').map(Number);
        detectedBgColor = [r, g, b];
      }
    }

    if (detectedBgColor) {
      console.log('Detected background color:', detectedBgColor);
    }
  }

  const isSimilarToBackground = (c) => {
    if (!detectedBgColor) return false;
    const dist = colorDistance(c, detectedBgColor);
    return dist < tolerance * 1.5; // More generous tolerance for detected bg color
  };

  // Flood fill from edges
  const floodFill = (startX, startY, baseTolerance) => {
    const startColor = getPixelColor(startX, startY);

    // Skip if already transparent
    if (startColor[3] === 0) return;

    // Determine tolerance based on color
    const tol = isWhitish(startColor) ? whiteTolerance : baseTolerance;

    const visited = new Set();
    const stack = [[startX, startY]];

    while (stack.length > 0) {
      const [x, y] = stack.pop();
      const key = `${x},${y}`;

      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      if (visited.has(key)) continue;

      const currentColor = getPixelColor(x, y);
      if (currentColor[3] === 0) continue; // Already transparent

      // Check if similar to start color OR is light background OR matches detected bg
      const dist = colorDistance(currentColor, startColor);
      const shouldRemove = dist < tol ||
                          (isLightBackground(currentColor) && isLightBackground(startColor)) ||
                          isSimilarToBackground(currentColor);

      if (!shouldRemove) continue;

      visited.add(key);
      setPixelTransparent(x, y);

      // Add neighbors (4-connected)
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
  };

  // Multiple passes from edges
  for (let pass = 0; pass < passes; pass++) {
    for (let x = 0; x < width; x++) {
      floodFill(x, 0, tolerance);
      floodFill(x, height - 1, tolerance);
    }
    for (let y = 0; y < height; y++) {
      floodFill(0, y, tolerance);
      floodFill(width - 1, y, tolerance);
    }
  }

  // Helper to check if pixel has transparent neighbor
  const hasTransparentNeighbor = (x, y) => {
    const neighbors = [[x+1,y], [x-1,y], [x,y+1], [x,y-1]];
    for (const [nx, ny] of neighbors) {
      if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
        const c = getPixelColor(nx, ny);
        if (c[3] === 0) return true;
      }
    }
    return false;
  };

  // Remove internal white areas (like lens areas) - multiple iterations with expanding threshold
  if (removeInternalWhite) {
    // First pass: remove very light pixels adjacent to transparent
    for (let iteration = 0; iteration < 20; iteration++) {
      let changed = false;
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const c = getPixelColor(x, y);
          if (c[3] > 0 && isVeryLight(c) && hasTransparentNeighbor(x, y)) {
            setPixelTransparent(x, y);
            changed = true;
          }
        }
      }
      if (!changed) break;
    }

    // Second pass: remove light gray areas (more aggressive)
    for (let iteration = 0; iteration < 15; iteration++) {
      let changed = false;
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const c = getPixelColor(x, y);
          if (c[3] > 0 && isLightGray(c) && hasTransparentNeighbor(x, y)) {
            setPixelTransparent(x, y);
            changed = true;
          }
        }
      }
      if (!changed) break;
    }
  }

  // Final cleanup - remove remaining whitish pixels at edges
  for (let iteration = 0; iteration < 10; iteration++) {
    let changed = false;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const c = getPixelColor(x, y);
        if (c[3] > 0 && isWhitish(c) && hasTransparentNeighbor(x, y)) {
          setPixelTransparent(x, y);
          changed = true;
        }
      }
    }
    if (!changed) break;
  }

  // AGGRESSIVE: Remove all pixels matching detected background color (even if not at edge)
  // This handles cases where background color is inside the image
  if (detectedBgColor) {
    for (let iteration = 0; iteration < 15; iteration++) {
      let changed = false;
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const c = getPixelColor(x, y);
          if (c[3] > 0 && isSimilarToBackground(c) && hasTransparentNeighbor(x, y)) {
            setPixelTransparent(x, y);
            changed = true;
          }
        }
      }
      if (!changed) break;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
};

/**
 * Isolate the main temple arm by keeping only the largest connected component
 * This removes the far temple, frame pieces, and other artifacts
 * Enhanced with better edge detection for dark frames
 */
export const isolateMainTemple = async (imageDataUrl, options = {}) => {
  const {
    alphaThreshold = 30, // Minimum alpha to consider as content
    usePositionBias = true, // Prefer components in expected temple position
    useShapeBias = true, // Prefer elongated horizontal shapes
  } = options;

  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const width = canvas.width;
  const height = canvas.height;

  // Create a label map for connected components
  const labels = new Int32Array(width * height);
  let nextLabel = 1;

  const getIdx = (x, y) => y * width + x;
  const hasContent = (x, y) => {
    const idx = getIdx(x, y) * 4;
    return data[idx + 3] > alphaThreshold;
  };

  // Get pixel brightness for edge detection
  const getPixelBrightness = (x, y) => {
    const idx = getIdx(x, y) * 4;
    return (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
  };

  // First pass: label connected components using flood fill
  const components = new Map();

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = getIdx(x, y);
      if (hasContent(x, y) && labels[idx] === 0) {
        const label = nextLabel++;
        const pixels = [];
        let minX = x, maxX = x, minY = y, maxY = y;
        let totalBrightness = 0;
        let centerXSum = 0, centerYSum = 0;

        const stack = [[x, y]];
        while (stack.length > 0) {
          const [cx, cy] = stack.pop();
          const cIdx = getIdx(cx, cy);

          if (cx < 0 || cx >= width || cy < 0 || cy >= height) continue;
          if (labels[cIdx] !== 0 || !hasContent(cx, cy)) continue;

          labels[cIdx] = label;
          pixels.push([cx, cy]);
          minX = Math.min(minX, cx);
          maxX = Math.max(maxX, cx);
          minY = Math.min(minY, cy);
          maxY = Math.max(maxY, cy);
          totalBrightness += getPixelBrightness(cx, cy);
          centerXSum += cx;
          centerYSum += cy;

          // 8-connected neighbors
          stack.push([cx+1, cy], [cx-1, cy], [cx, cy+1], [cx, cy-1]);
          stack.push([cx+1, cy+1], [cx-1, cy-1], [cx+1, cy-1], [cx-1, cy+1]);
        }

        const compWidth = maxX - minX + 1;
        const compHeight = maxY - minY + 1;

        components.set(label, {
          pixels,
          minX, maxX, minY, maxY,
          area: pixels.length,
          width: compWidth,
          height: compHeight,
          aspectRatio: compWidth / (compHeight || 1),
          centerX: centerXSum / pixels.length,
          centerY: centerYSum / pixels.length,
          avgBrightness: totalBrightness / pixels.length,
          // Normalized positions (0-1)
          normCenterX: (centerXSum / pixels.length) / width,
          normCenterY: (centerYSum / pixels.length) / height,
          horizontalSpan: compWidth / width,
          verticalSpan: compHeight / height,
        });
      }
    }
  }

  if (components.size === 0) {
    return imageDataUrl;
  }

  // Score each component to find the main temple
  let mainLabel = 1;
  let maxScore = 0;

  for (const [label, comp] of components) {
    let score = 0;

    // Base score: area (larger is better)
    const areaScore = Math.log(comp.area + 1) / Math.log(width * height);
    score += areaScore * 40;

    // Shape bias: prefer elongated horizontal shapes (temples are long and thin)
    if (useShapeBias) {
      // Aspect ratio: temples typically have aspect ratio 3-10
      const aspectScore = comp.aspectRatio >= 2 && comp.aspectRatio <= 15
        ? Math.min(comp.aspectRatio / 5, 1)
        : 0.3;
      score += aspectScore * 25;

      // Horizontal span: temple should span most of the width
      score += comp.horizontalSpan * 20;

      // Vertical position: temple is usually in the middle vertically
      const verticalCenteredness = 1 - Math.abs(comp.normCenterY - 0.5) * 2;
      score += verticalCenteredness * 10;
    }

    // Position bias: prefer components that match expected temple position
    // Temple typically spans left-to-right, hinge on right side
    if (usePositionBias) {
      // Hinge area (right side): bonus if component extends to the right
      const extendsToRight = comp.maxX > width * 0.7 ? 1 : 0.5;
      score += extendsToRight * 5;

      // Extends to left (ear tip): bonus if component extends to the left
      const extendsToLeft = comp.minX < width * 0.3 ? 1 : 0.5;
      score += extendsToLeft * 5;
    }

    // Penalize very small components (artifacts)
    if (comp.area < 100) {
      score *= 0.1;
    }

    // Penalize components that are mostly vertical (not temple-like)
    if (comp.aspectRatio < 1) {
      score *= 0.3;
    }

    // Penalize very tall components (likely frame front)
    if (comp.verticalSpan > 0.7) {
      score *= 0.5;
    }

    if (score > maxScore) {
      maxScore = score;
      mainLabel = label;
    }
  }

  const mainComponent = components.get(mainLabel);
  console.log(`Temple isolation: selected component with aspect ratio ${mainComponent?.aspectRatio?.toFixed(2)}, span ${(mainComponent?.horizontalSpan * 100)?.toFixed(1)}%`);

  // Remove all pixels not belonging to main component
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = getIdx(x, y);
      if (labels[idx] !== mainLabel && labels[idx] !== 0) {
        const pixelIdx = idx * 4;
        data[pixelIdx + 3] = 0;
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
};

/**
 * Remove white/light enclosed areas (lens regions) from front image
 * Makes the lens areas transparent
 */
export const removeWhiteLensAreas = async (imageDataUrl) => {
  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const width = canvas.width;
  const height = canvas.height;

  const getIdx = (x, y) => (y * width + x) * 4;

  const isWhiteOrLight = (x, y) => {
    const idx = getIdx(x, y);
    if (data[idx + 3] < 30) return false; // Already transparent
    const r = data[idx], g = data[idx + 1], b = data[idx + 2];
    const brightness = (r + g + b) / 3;
    // Check if it's white/light gray
    return brightness > 200 && Math.abs(r - g) < 30 && Math.abs(g - b) < 30;
  };

  const touchesEdge = (x, y) => {
    return x === 0 || x === width - 1 || y === 0 || y === height - 1;
  };

  // Find white regions that DON'T touch the edges (enclosed areas = lenses)
  const visited = new Set();
  const regionsToRemove = [];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const key = `${x},${y}`;
      if (visited.has(key) || !isWhiteOrLight(x, y)) continue;

      // Flood fill to find this white region
      const region = [];
      let touchesImageEdge = false;
      const stack = [[x, y]];

      while (stack.length > 0) {
        const [cx, cy] = stack.pop();
        const cKey = `${cx},${cy}`;

        if (cx < 0 || cx >= width || cy < 0 || cy >= height) continue;
        if (visited.has(cKey)) continue;
        if (!isWhiteOrLight(cx, cy)) continue;

        visited.add(cKey);
        region.push([cx, cy]);

        if (touchesEdge(cx, cy)) {
          touchesImageEdge = true;
        }

        // 4-connected neighbors
        stack.push([cx+1, cy], [cx-1, cy], [cx, cy+1], [cx, cy-1]);
      }

      // If region doesn't touch edge and is reasonably sized, it's likely a lens
      if (!touchesImageEdge && region.length > 100) {
        regionsToRemove.push(region);
      }
    }
  }

  // Make all lens regions transparent
  for (const region of regionsToRemove) {
    for (const [x, y] of region) {
      const idx = getIdx(x, y);
      data[idx + 3] = 0;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
};

/**
 * Crop image to content bounds
 * Enhanced with smarter alpha detection that ignores very faint pixels
 */
export const cropToContent = async (imageDataUrl, padding = 5) => {
  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const width = canvas.width;
  const height = canvas.height;

  // First pass: clean up very faint pixels (alpha 1-15) - likely artifacts
  // These can interfere with proper crop detection
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] > 0 && data[i] < 15) {
      data[i] = 0; // Make fully transparent
    }
  }
  ctx.putImageData(imageData, 0, 0);

  let minX = width, minY = height, maxX = 0, maxY = 0;

  // Detect bounds using alpha > 20 (lower threshold catches semi-transparent edges better)
  const alphaThreshold = 20;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const alpha = data[idx + 3];

      if (alpha > alphaThreshold) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (minX >= maxX || minY >= maxY) {
    console.log('cropToContent: No content found with threshold', alphaThreshold);
    return { dataUrl: imageDataUrl, bounds: null, originalSize: { width, height } };
  }

  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(width - 1, maxX + padding);
  maxY = Math.min(height - 1, maxY + padding);

  const cropWidth = maxX - minX + 1;
  const cropHeight = maxY - minY + 1;

  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = cropWidth;
  croppedCanvas.height = cropHeight;
  const croppedCtx = croppedCanvas.getContext('2d');

  croppedCtx.drawImage(
    canvas,
    minX, minY, cropWidth, cropHeight,
    0, 0, cropWidth, cropHeight
  );

  return {
    dataUrl: croppedCanvas.toDataURL('image/png'),
    bounds: { minX, minY, maxX, maxY, width: cropWidth, height: cropHeight },
    originalSize: { width, height },
  };
};

/**
 * Flip image horizontally
 */
export const flipImageHorizontal = async (imageDataUrl) => {
  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = img.width;
  canvas.height = img.height;

  ctx.translate(canvas.width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(img, 0, 0);

  return canvas.toDataURL('image/png');
};

/**
 * Detect frame/lens area by analyzing image shape
 * Returns crop bounds that exclude the frame area
 */
const detectFrameAreaFallback = async (imageDataUrl) => {
  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const width = canvas.width;
  const height = canvas.height;

  // Analyze each column to find content width (non-transparent pixels)
  const columnWidths = [];
  for (let x = 0; x < width; x++) {
    let minY = height, maxY = 0;
    for (let y = 0; y < height; y++) {
      const idx = (y * width + x) * 4;
      if (data[idx + 3] > 30) { // Has content
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    }
    columnWidths.push(maxY > minY ? maxY - minY : 0);
  }

  // Find the "wide" area (frame/lens) vs "narrow" area (temple arm)
  // The frame area typically has 2-3x the height of the temple arm
  const avgWidth = columnWidths.reduce((a, b) => a + b, 0) / columnWidths.length;
  const narrowThreshold = avgWidth * 0.7; // Consider "narrow" if below 70% of average

  // Scan from left and right to find where the "wide" part is
  let leftWideEnd = 0;
  let rightWideStart = width;

  // Find first narrow section from left (end of left frame area)
  let foundNarrow = false;
  for (let x = 0; x < width; x++) {
    if (columnWidths[x] > 0 && columnWidths[x] < narrowThreshold) {
      foundNarrow = true;
    } else if (foundNarrow && columnWidths[x] >= narrowThreshold * 1.5) {
      // Back to wide area
      leftWideEnd = x;
      break;
    }
    if (!foundNarrow && columnWidths[x] >= narrowThreshold * 1.5) {
      leftWideEnd = x;
    }
  }

  // Find first narrow section from right (end of right frame area)
  foundNarrow = false;
  for (let x = width - 1; x >= 0; x--) {
    if (columnWidths[x] > 0 && columnWidths[x] < narrowThreshold) {
      foundNarrow = true;
    } else if (foundNarrow && columnWidths[x] >= narrowThreshold * 1.5) {
      rightWideStart = x;
      break;
    }
    if (!foundNarrow && columnWidths[x] >= narrowThreshold * 1.5) {
      rightWideStart = x;
    }
  }

  // Determine which side has the frame (wider area)
  const leftMaxWidth = Math.max(...columnWidths.slice(0, Math.floor(width * 0.3)));
  const rightMaxWidth = Math.max(...columnWidths.slice(Math.floor(width * 0.7)));

  let cropLeft = 0, cropRight = width;

  if (leftMaxWidth > rightMaxWidth * 1.3) {
    // Frame is on left, crop it out
    cropLeft = Math.min(leftWideEnd + 10, width * 0.4);
  } else if (rightMaxWidth > leftMaxWidth * 1.3) {
    // Frame is on right, crop it out
    cropRight = Math.max(rightWideStart - 10, width * 0.6);
  }

  return {
    left: cropLeft / width,
    right: cropRight / width,
    top: 0,
    bottom: 1,
    frameDetected: leftMaxWidth > rightMaxWidth * 1.3 || rightMaxWidth > leftMaxWidth * 1.3,
    frameSide: leftMaxWidth > rightMaxWidth * 1.3 ? 'left' : (rightMaxWidth > leftMaxWidth * 1.3 ? 'right' : 'none'),
  };
};

/**
 * Extract temple from side view and ensure hinge is on right
 */
export const extractAndOrientTemple = async (imageDataUrl, analysis) => {
  let processedImage = imageDataUrl;

  // Get crop bounds from AI analysis or fallback detection
  let bounds = analysis?.cropBounds || analysis?.templeOnlyBounds;
  let needsFlip = analysis?.needsHorizontalFlip;

  // If AI didn't provide good bounds, use fallback detection
  if (!bounds || (bounds.left === 0 && bounds.right === 1)) {
    console.log('Using fallback frame detection...');
    const fallback = await detectFrameAreaFallback(processedImage);
    if (fallback.frameDetected) {
      bounds = fallback;
      // If frame is on right, hinge is on right, no flip needed
      // If frame is on left, hinge is on left, flip needed
      if (fallback.frameSide === 'left') {
        needsFlip = true;
      } else if (fallback.frameSide === 'right') {
        needsFlip = false;
      }
    }
  }

  // Crop to temple-only bounds if available (exclude frame front)
  if (bounds && bounds.left != null && bounds.right != null) {
    const img = await loadImage(processedImage);

    const x = Math.floor(bounds.left * img.width);
    const y = Math.floor((bounds.top || 0) * img.height);
    const w = Math.floor((bounds.right - bounds.left) * img.width);
    const h = Math.floor(((bounds.bottom || 1) - (bounds.top || 0)) * img.height);

    if (w > 20 && h > 10) {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
      processedImage = canvas.toDataURL('image/png');
    }
  }

  // Then flip if needed to put hinge on right side
  if (needsFlip) {
    processedImage = await flipImageHorizontal(processedImage);
  }

  return processedImage;
};

/**
 * Add glass/lens effect with detected tint color
 */
export const addGlassEffect = async (imageDataUrl, lensAreas, options = {}) => {
  const {
    tintColor = [180, 200, 220],
    tintOpacity = 0.12,
    reflectionOpacity = 0.18,
  } = options;

  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  if (!lensAreas || lensAreas.length === 0) {
    return canvas.toDataURL('image/png');
  }

  for (const lens of lensAreas) {
    const centerX = lens.centerX * canvas.width;
    const centerY = lens.centerY * canvas.height;
    const radiusX = lens.radiusX * canvas.width;
    const radiusY = lens.radiusY * canvas.height;

    ctx.save();
    ctx.beginPath();
    ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
    ctx.clip();

    // Add tint
    ctx.fillStyle = `rgba(${tintColor[0]}, ${tintColor[1]}, ${tintColor[2]}, ${tintOpacity})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add reflection highlight
    const reflectionGradient = ctx.createLinearGradient(
      centerX - radiusX * 0.6,
      centerY - radiusY * 0.6,
      centerX + radiusX * 0.4,
      centerY + radiusY * 0.4
    );
    reflectionGradient.addColorStop(0, `rgba(255, 255, 255, ${reflectionOpacity})`);
    reflectionGradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.02)');
    reflectionGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.fillStyle = reflectionGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.restore();
  }

  return canvas.toDataURL('image/png');
};

/**
 * Convert hinge positions from Gemini analysis to VTO format
 */
export const convertHingeToVTOFormat = (analysis, imageWidth, imageHeight, contentBounds = null) => {
  if (!analysis?.hingePositions) {
    return { hingeX: 0.95, hingeY: 0.02 };
  }

  const { left, right } = analysis.hingePositions;

  // hingeX in VTO is distance from CENTER of the VISIBLE frame (0=center, 1=edge).
  // If the image has transparent padding, use alpha-bounds to avoid systematic hinge errors.
  const b = (contentBounds && contentBounds.ok) ? contentBounds : null;
  const xMin = b ? b.xMin : 0;
  const xMax = b ? b.xMax : 1;
  const centerX = (xMin + xMax) / 2;
  const halfWidth = Math.max(1e-6, (xMax - xMin) / 2);

  const lx = left?.x ?? 0.05;
  const rx = right?.x ?? 0.95;
  const leftDistNorm = (centerX - lx) / halfWidth;
  const rightDistNorm = (rx - centerX) / halfWidth;
  const avgDistNorm = (leftDistNorm + rightDistNorm) / 2;
  const hingeX = Math.min(1, Math.max(0, avgDistNorm));

  // hingeY: convert from normalized (0-1) to world coordinates
  const aspectRatio = imageWidth / imageHeight;
  const worldHeight = 2 / aspectRatio;
  const avgY = ((left?.y || 0.4) + (right?.y || 0.4)) / 2;
  const hingeY = (worldHeight / 2) - (avgY * worldHeight);

  return {
    hingeX: Number(Math.max(0.8, Math.min(1, hingeX)).toFixed(3)),
    hingeY: Number(hingeY.toFixed(3)),
  };
};

/**
 * Full image processing pipeline with adaptive strategies
 * UPDATED: Streamlined to trust Gemini 3 and skip redundant/damaging steps
 */
export const processEyewearImages = async (frontImageDataUrl, sideImageDataUrl, options = {}) => {
  const {
    removeBackgroundEnabled = true,
    cropEnabled = true,
    detectHinges = true,
    addGlassEffectEnabled = true,
    useWorkersAI = true,
    useAITempleCleanup = true,
    useAIFrontCleanup = true,
    generateMissingSide = true,
    angleImageDataUrl = null,
    attemptNumber = 1,
    onProgress = () => {},
    glassesType = 'auto', // 'auto' | 'sunglasses' | 'prescription'
    sunglassesOpacity = 0.5,
    enhancementPreset = 'neutral', // 'neutral' | 'standard' | 'vivid' (manual fallback only)
  } = options;

  const results = {
    frontImage: null,
    handleImage: null,
    hingeX: 0.95,
    hingeY: 0.02,
    analysis: null,
    isSunglasses: false,
    sideGenerated: false,
    aiCleaned: false, 
    frontAiCleaned: false, 
    processingStrategy: null, 
    qualityChecks: [], 
    errors: [],
    usage: {
      promptTokenCount: 0,
      candidatesTokenCount: 0,
      totalTokenCount: 0,
    },
    cost: 0,
    model: GEMINI_MODEL,
  };

  const addUsage = (analysisResult) => {
    if (analysisResult?._usage) {
      results.usage.promptTokenCount += analysisResult._usage.promptTokenCount || 0;
      results.usage.candidatesTokenCount += analysisResult._usage.candidatesTokenCount || 0;
      results.usage.totalTokenCount += analysisResult._usage.totalTokenCount || 0;
      results.cost += analysisResult._cost || 0;
    }
  };

  try {
    // Step 1: Analyze front image
    onProgress({ step: 'analyzing', message: 'Analyzing front image with AI...' });

    let frontAnalysis = null;
    let lensTintColor = null;

    if (detectHinges && frontImageDataUrl) {
      try {
        frontAnalysis = await analyzeEyewearImage(frontImageDataUrl, 'front');
        addUsage(frontAnalysis);
        results.analysis = { front: frontAnalysis };

        if (frontAnalysis?.frameAnalysis?.hasTintedLenses && frontAnalysis?.frameAnalysis?.lensTintColor) {
          lensTintColor = frontAnalysis.frameAnalysis.lensTintColor;
        }

        const frontImg = await loadImage(frontImageDataUrl);
        const frontBounds = computeAlphaBoundsFromImage(frontImg, { alphaThreshold: 20, maxScanSize: 512 });
        // Expose bounds so the dashboard can store them (used later for sizing corrections)
        if (frontBounds?.ok) {
          results.frontContentScaleX = frontBounds.scaleX;
          results.frontContentScaleY = frontBounds.scaleY;
          results.frontContentBBox = JSON.stringify({
            xMin: frontBounds.xMin, yMin: frontBounds.yMin, xMax: frontBounds.xMax, yMax: frontBounds.yMax,
          });
        }
        const hingeResult = convertHingeToVTOFormat(frontAnalysis, frontImg.width, frontImg.height, frontBounds);
        results.hingeX = hingeResult.hingeX;
        results.hingeY = hingeResult.hingeY;
      } catch (err) {
        console.error('Front analysis error:', err);
        results.errors.push({ step: 'front-analysis', error: err.message });
      }
    }

    // Step 2: Process front image
    onProgress({ step: 'processing-front', message: 'Processing front image...' });

    if (frontImageDataUrl) {
      let processedFront = frontImageDataUrl;
      let aiSuccess = false;

      // Determine if this is sunglasses based on user selection or auto-detection
      const autoDetectedSunglasses = frontAnalysis?.frameAnalysis?.isSunglasses ||
                                     frontAnalysis?.frameAnalysis?.hasTintedLenses;
      const isSunglasses = glassesType === 'sunglasses' ? true :
                           glassesType === 'prescription' ? false :
                           autoDetectedSunglasses; // 'auto' mode uses detection

      // STEP 2a: AI regeneration FIRST
      if (useAIFrontCleanup && GEMINI_API_KEY) {
        const cleanupMsg = isSunglasses
          ? 'Gemini 3 processing sunglasses (preserving tinted lenses)...'
          : 'Gemini 3 generating perfect transparent frame...';
        onProgress({ step: 'processing-front', message: cleanupMsg });
        try {
          const cleaned = await cleanupFrontFrameWithAI(processedFront, {
            isSunglasses,
            lensOpacity: sunglassesOpacity,
          });
          processedFront = cleaned.dataUrl;
          addUsage(cleaned);
          results.frontAiCleaned = true;
          aiSuccess = true; 
          console.log('AI regeneration successful');
        } catch (err) {
          console.error('AI front frame cleanup error:', err);
          results.errors.push({ step: 'ai-front-cleanup', error: err.message });
        }
      }

      // STEP 2b: Manual Processing (ONLY if AI failed or was disabled)
      if (!aiSuccess && removeBackgroundEnabled) {
        try {
          if (useWorkersAI) {
            onProgress({ step: 'processing-front', message: 'Removing background with Workers AI...' });
            processedFront = await removeBackgroundWorkersAI(processedFront, { enableRetry: true });
          } else {
            processedFront = await removeBackgroundStrong(processedFront);
          }

          // DANGEROUS FUNCTION: Only run if AI didn't already handle the lenses
          processedFront = await removeWhiteLensAreas(processedFront);
          
          // EDGE CLEANUP: Only run on manual results (AI results are usually clean)
          processedFront = await cleanupTransparentEdges(processedFront, {
            edgeWidth: 1,
            alphaThreshold: 220, 
          });

        } catch (err) {
          console.error('Background removal error:', err);
          results.errors.push({ step: 'front-bg-removal', error: err.message });
        }
      }

      if (processedFront && isSunglasses) {
        try {
          onProgress({ step: 'processing-front', message: 'Tinting lens holes...' });
          // Use detected color OR default to Dark Grey [60, 60, 70]
          const finalTint = lensTintColor || [60, 60, 70];
          processedFront = await tintLensHoles(processedFront, 0.60, finalTint);
        } catch (err) {
          console.warn('Lens tinting failed:', err);
        }
     }

      // Crop is safe to run on both AI and Manual results
      if (cropEnabled) {
        try {
          onProgress({ step: 'processing-front', message: 'Cropping front frame...' });
          const cropped = await cropToContent(processedFront, 10);
          processedFront = cropped.dataUrl;
        } catch (err) {
          console.error('Crop error:', err);
          results.errors.push({ step: 'front-crop', error: err.message });
        }
      }

      // Glass effect is an overlay, safe to add to AI results
      if (addGlassEffectEnabled && frontAnalysis?.lensAreas?.length > 0) {
        try {
          onProgress({ step: 'processing-front', message: 'Adding glass effect...' });
          const glassOptions = isSunglasses
            ? { tintColor: lensTintColor || [80, 80, 90], tintOpacity: 0.25, reflectionOpacity: 0.12 }
            : { tintColor: [200, 220, 240], tintOpacity: 0.08, reflectionOpacity: 0.15 };
          processedFront = await addGlassEffect(processedFront, frontAnalysis.lensAreas, glassOptions);
        } catch (err) {
          console.error('Glass effect error:', err);
        }
      }

      // Validation
      try {
        const validation = await validateImageQuality(processedFront, {
          minWidth: 150, minHeight: 80, minOpaquePixels: 1000, requireTransparentBackground: true,
        });
        results.qualityChecks.push({ step: 'front-final', ...validation });
      } catch (err) { console.warn('Front quality validation failed:', err.message); }

      results.frontImage = processedFront;
      results.isSunglasses = isSunglasses;
    }

    // Step 3: Handle side/temple image
    let sideAnalysis = null;
    let workingSideImage = sideImageDataUrl;

    if (!workingSideImage && generateMissingSide && frontImageDataUrl) {
      onProgress({ step: 'generating-side', message: 'Generating temple image from front view with AI...' });
      try {
        const generated = await generateMissingSideImage(frontImageDataUrl, angleImageDataUrl);
        workingSideImage = generated.dataUrl;
        addUsage(generated);
        results.sideGenerated = true;
      } catch (err) {
        console.error('Side image generation error:', err);
        results.errors.push({ step: 'side-generation', error: err.message });
      }
    }

    if (workingSideImage && detectHinges) {
      try {
        sideAnalysis = await analyzeEyewearImage(workingSideImage, 'side');
        addUsage(sideAnalysis);
        results.analysis = { ...results.analysis, side: sideAnalysis };
      } catch (err) { console.error('Side analysis error:', err); }
    }

    // Step 4: Process side/temple image
    if (workingSideImage) {
      onProgress({ step: 'processing-side', message: 'Processing temple image...' });
      let processedSide = workingSideImage;

      // AI-powered temple cleanup (The "Identity" Pass)
      if (useAITempleCleanup && GEMINI_API_KEY) {
        onProgress({ step: 'ai-cleanup', message: 'Gemini generating isolated handle...' });
        try {
          const cleaned = await cleanupTempleWithAI(processedSide);
          processedSide = cleaned.dataUrl;
          addUsage(cleaned);
          results.aiCleaned = true;
          // AI succeeded: We trust the output is transparent and identical
        } catch (err) {
          console.error('AI temple cleanup error:', err);
          results.errors.push({ step: 'ai-temple-cleanup', error: err.message });
          results.aiCleaned = false;
        }
      }

      // MANUAL FALLBACKS: Only run if AI didn't run or failed
      if (!results.aiCleaned) {
        if (removeBackgroundEnabled) {
          try {
            if (useWorkersAI) {
               processedSide = await removeBackgroundWorkersAI(processedSide, { enableRetry: true });
            } else {
               processedSide = await removeBackgroundStrong(processedSide);
            }
          } catch (err) { console.error('Side bg removal error:', err); }
        }

        try {
          processedSide = await extractAndOrientTemple(processedSide, sideAnalysis);
        } catch (err) { console.error('Temple extraction error:', err); }

        try {
          processedSide = await isolateMainTemple(processedSide);
        } catch (err) { console.error('Temple isolation error:', err); }

        // Enhancements (Sharpening/Contrast) - ONLY for manual results
        // We skip this for AI results to keep them "identical"
        try {
          processedSide = await cleanupTransparentEdges(processedSide, { edgeWidth: 2, alphaThreshold: 200 });
          const preset = String(enhancementPreset || 'neutral');
          const enhanceOpts =
            preset === 'vivid'
              ? { contrast: 1.08, brightness: 1.0, saturation: 1.03, sharpen: true, removeColorCast: true }
              : preset === 'standard'
                ? { contrast: 1.04, brightness: 1.0, saturation: 1.01, sharpen: true, removeColorCast: true }
                : { contrast: 1.0, brightness: 1.0, saturation: 1.0, sharpen: false, removeColorCast: true };
          processedSide = await enhanceImageQuality(processedSide, enhanceOpts);
        } catch (err) { console.warn('Temple enhancement failed:', err.message); }
      }

      // Always crop at the end (safe for both)
      if (cropEnabled) {
        try {
          const cropped = await cropToContent(processedSide);
          processedSide = cropped.dataUrl;
        } catch (err) { console.error('Side crop error:', err); }
      }

      // Compute alpha-bounds for the temple (helps correct length if there is transparent padding)
      try {
        const sideImg = await loadImage(processedSide);
        const sideBounds = computeAlphaBoundsFromImage(sideImg, { alphaThreshold: 20, maxScanSize: 512 });
        if (sideBounds?.ok) {
          results.handleContentScaleX = sideBounds.scaleX;
          results.handleContentScaleY = sideBounds.scaleY;
          results.handleContentBBox = JSON.stringify({
            xMin: sideBounds.xMin, yMin: sideBounds.yMin, xMax: sideBounds.xMax, yMax: sideBounds.yMax,
          });
        }
      } catch {
        // ignore
      }

      results.handleImage = processedSide;
    }

    // ... (Validation Summary Logic remains the same) ...

    onProgress({ step: 'complete', message: 'Processing complete!' });
    return results;

  } catch (error) {
    console.error('Processing pipeline error:', error);
    results.errors.push({ step: 'pipeline', error: error.message });
    throw error;
  }
};

/**
 * Smartly detects empty "holes" inside the frame (lenses) and fills them with tint.
 * Uses Flood Fill to distinguish the "outside" background from "inside" holes.
 */
export const tintLensHoles = async (imageDataUrl, opacity = 0.5, tintColor = [50, 50, 60]) => {
  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const width = canvas.width;
  const height = canvas.height;

  // 1. Identify the "Outside" Background using Flood Fill from corner (0,0)
  const visited = new Uint8Array(width * height); // 0 = unvisited, 1 = background
  const stack = [[0, 0]]; // Start at top-left
  const getIdx = (x, y) => (y * width + x);

  while (stack.length > 0) {
    const [x, y] = stack.pop();
    const idx = getIdx(x, y);

    if (x < 0 || x >= width || y < 0 || y >= height) continue;
    if (visited[idx]) continue;

    // Check if pixel is transparent (Alpha < 20)
    // If transparent, it's background. If opaque, it's the frame (a wall).
    const alpha = data[idx * 4 + 3];
    if (alpha > 20) continue; 

    visited[idx] = 1; // Mark as background

    // Add neighbors
    stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
  }

  // 2. Tint the "Inside" Holes
  // Any pixel that is transparent (Alpha < 20) BUT NOT visited is a Lens Hole!
  for (let i = 0; i < width * height; i++) {
    const alpha = data[i * 4 + 3];
    
    if (alpha < 20 && visited[i] === 0) {
      data[i * 4] = tintColor[0];     // R
      data[i * 4 + 1] = tintColor[1]; // G
      data[i * 4 + 2] = tintColor[2]; // B
      data[i * 4 + 3] = Math.round(255 * opacity); // Apply Tint Opacity
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
};

/**
 * Check if Gemini API is available
 */
export const isGeminiAvailable = () => {
  return !!GEMINI_API_KEY;
};

export default {
  analyzeEyewearImage,
  removeBackgroundStrong,
  removeBackgroundWorkersAI,
  removeWhiteLensAreas,
  isolateMainTemple,
  cropToContent,
  flipImageHorizontal,
  extractAndOrientTemple,
  addGlassEffect,
  convertHingeToVTOFormat,
  processEyewearImages,
  isGeminiAvailable,
  // AI-powered functions
  cleanupTempleWithAI,
  cleanupFrontFrameWithAI,
  generateMissingSideImage,
  applySunglassLensOpacity,
  // Validation & quality check functions
  analyzeImageContent,
  validateImageQuality,
  determineProcessingStrategy,
  compareProcessingResult,
  // Image enhancement functions
  enhanceImageQuality,
  cleanupTransparentEdges,
};
